(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.FamilyInlineCrop = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function normalizeRect(rect) {
    const x = clamp(Number(rect?.x) || 0, 0, 1);
    const y = clamp(Number(rect?.y) || 0, 0, 1);
    const width = clamp(Number(rect?.width) || 0, 0, 1 - x);
    const height = clamp(Number(rect?.height) || 0, 0, 1 - y);
    return { x, y, width, height };
  }

  function moveRect(rect, dx, dy) {
    const current = normalizeRect(rect);
    return normalizeRect({
      ...current,
      x: clamp(current.x + dx, 0, 1 - current.width),
      y: clamp(current.y + dy, 0, 1 - current.height)
    });
  }

  function resizeRect(rect, handle, dx, dy, minSize = 0.03) {
    const current = normalizeRect(rect);
    let left = current.x;
    let top = current.y;
    let right = current.x + current.width;
    let bottom = current.y + current.height;
    if (handle.includes('w')) left = clamp(left + dx, 0, right - minSize);
    if (handle.includes('e')) right = clamp(right + dx, left + minSize, 1);
    if (handle.includes('n')) top = clamp(top + dy, 0, bottom - minSize);
    if (handle.includes('s')) bottom = clamp(bottom + dy, top + minSize, 1);
    return normalizeRect({ x: left, y: top, width: right - left, height: bottom - top });
  }

  // Ratios use source pixels; normalized x/y coordinates have different scales.
  function fitAspectRect(rect, ratio) {
    const current = normalizeRect(rect);
    const width = Math.min(current.width, current.height * ratio);
    const height = width / ratio;
    return normalizeRect({ x: current.x + (current.width - width) / 2, y: current.y + (current.height - height) / 2, width, height });
  }

  function resizeAspectRect(rect, handle, dx, dy, ratio) {
    const current = normalizeRect(rect);
    const west = handle.includes('w');
    const north = handle.includes('n');
    const horizontal = west || handle.includes('e');
    const vertical = north || handle.includes('s');
    const anchorX = horizontal ? current.x + (west ? current.width : 0) : current.x + current.width / 2;
    const anchorY = vertical ? current.y + (north ? current.height : 0) : current.y + current.height / 2;
    const changeX = (west ? -dx : dx);
    const changeY = (north ? -dy : dy) * ratio;
    const change = horizontal && vertical ? (Math.abs(changeX) >= Math.abs(changeY) ? changeX : changeY) : horizontal ? changeX : changeY;
    const maxWidthX = horizontal ? (west ? anchorX : 1 - anchorX) : 2 * Math.min(anchorX, 1 - anchorX);
    const maxHeightY = vertical ? (north ? anchorY : 1 - anchorY) : 2 * Math.min(anchorY, 1 - anchorY);
    const maxWidth = Math.min(maxWidthX, maxHeightY * ratio);
    const width = clamp(current.width + change, Math.min(.03, maxWidth), maxWidth);
    const height = width / ratio;
    return normalizeRect({ x: horizontal ? anchorX - (west ? width : 0) : anchorX - width / 2, y: vertical ? anchorY - (north ? height : 0) : anchorY - height / 2, width, height });
  }

  function buildEntries(files, captions = [], maxPhotos = 4) {
    return (Array.isArray(files) ? files : []).slice(0, Math.min(8, maxPhotos)).map((file, slot) => file ? {
      slot,
      file,
      caption: typeof captions[slot] === 'string' ? captions[slot].trim().slice(0, 120) : ''
    } : null).filter(Boolean);
  }

  function acceptedPhotoCount(files, maxPhotos = 4) {
    return (Array.isArray(files) ? files : []).slice(0, Math.min(8, maxPhotos)).filter(Boolean).length;
  }

  function firstEmptySlot(files, maxPhotos = 4) {
    const slots = Array.isArray(files) ? files.slice(0, Math.min(8, maxPhotos)) : [];
    return slots.findIndex(file => !file);
  }

  function buildRequestMetadata(entries, additionalImages) {
    const metadata = { photoCaptions: entries.map(entry => String(entry.caption || '').trim()) };
    if (additionalImages !== undefined) {
      const urls = additionalImages.slice();
      if (urls.length !== Math.max(0, entries.length - 1)) {
        throw new Error('The number of uploaded crop files no longer matches the photo captions.');
      }
      metadata.additionalImages = urls;
    }
    return metadata;
  }

  function canReuseFullFrameFile(file, rect) {
    const crop = normalizeRect(rect);
    return crop.x === 0 && crop.y === 0 && crop.width === 1 && crop.height === 1 && /^(image\/(jpeg|png|webp))$/i.test(file?.type || '');
  }

  function mount(host, file, options = {}) {
    if (!host || !file) throw new TypeError('A crop host and image file are required.');
    let fullFrameSnapshot = null;
    const url = URL.createObjectURL(file);
    const image = document.createElement('img');
    image.className = 'family-crop-image';
    image.alt = options.alt || 'Uploaded photo crop';
    image.draggable = false;
    image.decoding = 'async';
    image.loading = 'eager';
    // A loaded image with real dimensions can render without waiting for
    // decode(), which may remain pending on a hidden preview in a browser.
    let cancelImageLoad;
    let removeImageLoadListeners;
    const loaded = new Promise((resolve, reject) => {
      const onLoad = () => {
        if (image.naturalWidth && image.naturalHeight) resolve();
        else reject(new Error('Photo has no dimensions'));
      };
      const onError = () => reject(new Error('Photo could not load'));
      removeImageLoadListeners = () => {
        image.removeEventListener('load', onLoad);
        image.removeEventListener('error', onError);
      };
      cancelImageLoad = () => reject(new Error('Cropper has been removed.'));
      image.addEventListener('load', onLoad);
      image.addEventListener('error', onError);
    });
    // mount callers may cancel before calling ready().
    loaded.catch(() => {});
    image.src = url;
    const viewport = document.createElement('div');
    viewport.className = 'family-crop-viewport';
    viewport.hidden = true;
    const shade = document.createElement('div');
    shade.className = 'family-crop-shade';
    ['top', 'right', 'bottom', 'left'].forEach(edge => {
      const mask = document.createElement('div');
      mask.className = `family-crop-shade-${edge}`;
      shade.append(mask);
    });
    const selection = document.createElement('div');
    selection.className = 'family-crop-selection';
    selection.setAttribute('role', 'group');
    selection.setAttribute('aria-label', 'Crop selection. Use arrow keys on a corner handle to adjust the crop.');
    const handles = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
    const handleButtons = new Map();
    handles.forEach((handle) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `family-crop-handle family-crop-handle-${handle}`;
      button.dataset.handle = handle;
      button.setAttribute('aria-label', `Adjust ${handle} crop edge`);
      button.title = `Adjust ${handle} crop edge`;
      selection.append(button);
      handleButtons.set(handle, button);
    });
    viewport.append(image, shade, selection);
    host.replaceChildren(viewport);

    let rect = normalizeRect(options.initialRect || { x: 0, y: 0, width: 1, height: 1 });
    const aspectRatio = Number(options.aspectRatio) > 0 ? Number(options.aspectRatio) : null;
    let normalizedRatio = null;
    let drag = null;
    let destroyed = false;
    let listenersActive = true;
    let imageReady = false;
    let readyPromise = null;

    function fitImage() {
      if (!imageReady || destroyed) return;
      const available = Math.max(1, (host.clientWidth || host.parentElement?.clientWidth || window.innerWidth - 44) - 44);
      const scale = Math.min(1, available / image.naturalWidth, window.innerHeight * .52 / image.naturalHeight);
      viewport.style.width = `${image.naturalWidth * scale}px`;
      viewport.style.height = `${image.naturalHeight * scale}px`;
    }
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(fitImage) : null;
    resizeObserver?.observe(host);
    window.addEventListener('resize', fitImage);

    function render() {
      selection.style.left = `${rect.x * 100}%`;
      selection.style.top = `${rect.y * 100}%`;
      selection.style.width = `${rect.width * 100}%`;
      selection.style.height = `${rect.height * 100}%`;
      const right = rect.x + rect.width;
      const bottom = rect.y + rect.height;
      const [topMask, rightMask, bottomMask, leftMask] = shade.children;
      Object.assign(topMask.style, { left: '0', top: '0', width: '100%', height: `${rect.y * 100}%` });
      Object.assign(rightMask.style, { left: `${right * 100}%`, top: `${rect.y * 100}%`, width: `${(1 - right) * 100}%`, height: `${rect.height * 100}%` });
      Object.assign(bottomMask.style, { left: '0', top: `${bottom * 100}%`, width: '100%', height: `${(1 - bottom) * 100}%` });
      Object.assign(leftMask.style, { left: '0', top: `${rect.y * 100}%`, width: `${rect.x * 100}%`, height: `${rect.height * 100}%` });
    }

    function beginDrag(event, id, x, y) {
      if (!imageReady || drag || (event.button !== undefined && event.button !== 0)) return;
      const handle = event.target.closest('[data-handle]')?.dataset.handle || 'move';
      drag = { id, handle, x, y, rect: { ...rect }, bounds: viewport.getBoundingClientRect() };
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();
      if (event.pointerId !== undefined) selection.setPointerCapture?.(event.pointerId);
    }
    function moveDrag(event, id, x, y) {
      if (!drag || id !== drag.id) return;
      if (event.cancelable) event.preventDefault();
      const dx = (x - drag.x) / Math.max(drag.bounds.width, 1);
      const dy = (y - drag.y) / Math.max(drag.bounds.height, 1);
      rect = drag.handle === 'move' ? moveRect(drag.rect, dx, dy) : normalizedRatio ? resizeAspectRect(drag.rect, drag.handle, dx, dy, normalizedRatio) : resizeRect(drag.rect, drag.handle, dx, dy);
      render();
    }
    function endDrag(id) { if (drag?.id === id) drag = null; }
    function onPointerDown(event) { beginDrag(event, event.pointerId, event.clientX, event.clientY); }
    function onPointerMove(event) { moveDrag(event, event.pointerId, event.clientX, event.clientY); }
    function onPointerUp(event) { endDrag(event.pointerId); }
    function onPointerCancel(event) { endDrag(event.pointerId); }
    function onTouchStart(event) {
      const touch = event.changedTouches[0];
      if (touch) beginDrag(event, touch.identifier, touch.clientX, touch.clientY);
    }
    function onTouchMove(event) {
      const touch = [...event.changedTouches].find(item => item.identifier === drag?.id);
      if (touch) moveDrag(event, touch.identifier, touch.clientX, touch.clientY);
    }
    function onTouchEnd(event) { for (const touch of event.changedTouches) endDrag(touch.identifier); }
    const usePointerEvents = typeof window.PointerEvent === 'function';
    function onKeyDown(event) {
      const handle = event.currentTarget.dataset.handle;
      const step = event.shiftKey ? 0.05 : 0.01;
      const delta = { x: 0, y: 0 };
      if (event.key === 'ArrowLeft') delta.x = -step;
      else if (event.key === 'ArrowRight') delta.x = step;
      else if (event.key === 'ArrowUp') delta.y = -step;
      else if (event.key === 'ArrowDown') delta.y = step;
      else return;
      event.preventDefault();
      rect = normalizedRatio ? resizeAspectRect(rect, handle, delta.x, delta.y, normalizedRatio) : resizeRect(rect, handle, delta.x, delta.y);
      render();
    }

    function removeListeners() {
      if (!listenersActive) return;
      listenersActive = false;
      selection.removeEventListener('pointerdown', onPointerDown);
      selection.removeEventListener('lostpointercapture', onPointerCancel);
      selection.removeEventListener('touchstart', onTouchStart);
      selection.removeEventListener('touchmove', onTouchMove);
      selection.removeEventListener('touchend', onTouchEnd);
      selection.removeEventListener('touchcancel', onTouchEnd);
      window.removeEventListener('resize', fitImage);
      resizeObserver?.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      handleButtons.forEach((button) => button.removeEventListener('keydown', onKeyDown));
    }

    if (usePointerEvents) {
      selection.addEventListener('pointerdown', onPointerDown);
      selection.addEventListener('lostpointercapture', onPointerCancel);
      window.addEventListener('pointermove', onPointerMove, { passive: false });
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerCancel);
    } else {
      selection.addEventListener('touchstart', onTouchStart, { passive: false });
      selection.addEventListener('touchmove', onTouchMove, { passive: false });
      selection.addEventListener('touchend', onTouchEnd);
      selection.addEventListener('touchcancel', onTouchEnd);
    }
    handleButtons.forEach((button) => button.addEventListener('keydown', onKeyDown));
    render();

    return {
      getRect: () => ({ ...rect }),
      getFile: () => file,
      reset() { rect = normalizedRatio ? fitAspectRect({ x: 0, y: 0, width: 1, height: 1 }, normalizedRatio) : { x: 0, y: 0, width: 1, height: 1 }; render(); },
      async ready() {
        if (destroyed) throw new Error('Cropper has been removed.');
        const decodeStarted = Date.now();
        try {
          if (!readyPromise) {
            const decoded = typeof image.decode === 'function'
              ? Promise.resolve().then(() => image.decode()) : loaded;
            const available = Promise.race([loaded, decoded,
              ...(image.complete && image.naturalWidth && image.naturalHeight ? [Promise.resolve()] : [])]);
            readyPromise = (window.CustomifyPhotoUpload
              ? window.CustomifyPhotoUpload.withTimeout(available) : available)
              .finally(removeImageLoadListeners);
          }
          await readyPromise;
        } catch (failure) {
          const error = failure?.code ? failure : new Error('Photo upload: file_decode');
          if (!error.code) error.code = 'file_decode';
          const allowedNames = ['EncodingError', 'NotReadableError', 'NotFoundError', 'SecurityError', 'AbortError', 'InvalidStateError', 'TypeError'];
          error.details = { ...error.details, decodeMs: Math.max(0, Date.now() - decodeStarted),
            nativeErrorName: allowedNames.includes(failure?.name) ? failure.name : 'Other' };
          throw error;
        }
        if (destroyed) throw new Error('Cropper has been removed.');
        if (!image.naturalWidth || !image.naturalHeight) throw new Error('This photo could not be opened.');
        if (!imageReady && aspectRatio) {
          normalizedRatio = aspectRatio * image.naturalHeight / image.naturalWidth;
          rect = fitAspectRect(rect, normalizedRatio);
          render();
        }
        imageReady = true;
        fitImage();
        viewport.hidden = false;
        return { width: image.naturalWidth, height: image.naturalHeight };
      },
      async toFile(outputName = file.name.replace(/\.[^.]+$/, '') + '-crop.jpg') {
        if (destroyed) throw new Error('Cropper has been removed.');
        await this.ready();
        if (destroyed) throw new Error('Cropper has been removed.');
        const crop = normalizeRect(rect);
        if (canReuseFullFrameFile(file, crop)) {
          // A device/cloud-backed File can stop being readable after selection.
          // Materialize its bytes while Done is pending; keep the original encoding.
          if (!fullFrameSnapshot) {
            try {
              const read = file.arrayBuffer();
              const bytes = await (window.CustomifyPhotoUpload ? window.CustomifyPhotoUpload.withTimeout(read) : read);
              if (!bytes.byteLength || bytes.byteLength !== file.size) throw new Error('Incomplete photo read');
              if (destroyed) throw new Error('Cropper has been removed.');
              fullFrameSnapshot = new File([bytes], file.name, { type: file.type, lastModified: file.lastModified });
            } catch (failure) {
              if (failure?.code) throw failure;
              const error = new Error('Photo upload: file_read');
              error.code = 'file_read';
              throw error;
            }
          }
          return fullFrameSnapshot;
        }
        const sourceWidth = image.naturalWidth;
        const sourceHeight = image.naturalHeight;
        const sx = Math.round(crop.x * sourceWidth);
        const sy = Math.round(crop.y * sourceHeight);
        const sw = Math.max(1, Math.min(sourceWidth - sx, Math.round(crop.width * sourceWidth)));
        const sh = Math.max(1, Math.min(sourceHeight - sy, Math.round(crop.height * sourceHeight)));
        const canvas = document.createElement('canvas');
        canvas.width = sw;
        canvas.height = aspectRatio === 1 ? canvas.width : sh;
        const context = canvas.getContext('2d', { alpha: false });
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error('Could not crop this photo.')), 'image/jpeg', 0.94));
        const output = new File([blob], outputName, { type: 'image/jpeg', lastModified: Date.now() });
        return output.size > 10 * 1024 * 1024 && window.CustomifyPhotoUpload?.prepareCropFile
          ? window.CustomifyPhotoUpload.prepareCropFile(output) : output;
      },
      closeEditor() { removeListeners(); drag = null; host.replaceChildren(); },
      destroy() {
        destroyed = true;
        cancelImageLoad();
        removeImageLoadListeners();
        removeListeners();
        URL.revokeObjectURL(url);
        host.replaceChildren();
      }
    };
  }

  let activeDialog = false;

  function openDialog(file, options = {}) {
    if (!file) return Promise.reject(new TypeError('An image file is required.'));
    if (activeDialog) return Promise.reject(new Error('Finish the open crop dialog first.'));
    activeDialog = true;
    return new Promise((resolve, reject) => {
      const labels = options.labels || {};
      const previousFocus = options.returnFocus || document.activeElement;
      const scrollPosition = { x: window.scrollX, y: window.scrollY };
      const bodyProperties = ['position', 'top', 'left', 'width', 'overflow'];
      const previousBody = bodyProperties.map(key => [key, document.body.style.getPropertyValue(key), document.body.style.getPropertyPriority(key)]);
      const previousRootOverflow = [document.documentElement.style.getPropertyValue('overflow'), document.documentElement.style.getPropertyPriority('overflow')];
      const overlay = document.createElement('div');
      overlay.className = 'family-crop-modal-backdrop';
      overlay.setAttribute('role', 'presentation');
      const dialog = document.createElement('section');
      dialog.className = 'family-crop-dialog';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', 'familyCropDialogTitle');
      dialog.setAttribute('aria-describedby', 'familyCropDialogHelp');
      dialog.tabIndex = -1;
      const heading = document.createElement('h2');
      heading.id = 'familyCropDialogTitle';
      heading.className = 'family-crop-dialog-title';
      heading.tabIndex = -1;
      heading.textContent = options.title || 'Crop this photo';
      const help = document.createElement('p');
      help.id = 'familyCropDialogHelp';
      help.className = 'family-crop-dialog-help';
      help.textContent = labels.help || 'Select the people to include';
      const errorNotice = document.createElement('p');
      errorNotice.className = 'family-crop-dialog-error';
      if (window.CustomifyPhotoUpload) window.CustomifyPhotoUpload.styleFeedback(errorNotice);
      else { errorNotice.setAttribute('role', 'alert'); errorNotice.tabIndex = -1; }
      errorNotice.hidden = true;
      errorNotice.style.display = 'none';
      const host = document.createElement('div');
      host.className = 'family-crop-dialog-image';
      const loading = document.createElement('p');
      loading.className = 'family-crop-loading';
      loading.textContent = labels.loading || 'Preparing photo…';
      loading.setAttribute('role', 'status');
      host.append(loading);
      const actions = document.createElement('div');
      actions.className = 'family-crop-dialog-actions';
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'family-crop-dialog-cancel';
      cancel.textContent = labels.cancel || 'Cancel';
      const useCrop = document.createElement('button');
      useCrop.type = 'button';
      useCrop.className = 'family-crop-dialog-use';
      useCrop.textContent = labels.done || 'Done';
      useCrop.disabled = true;
      actions.append(cancel, useCrop);
      dialog.append(heading, help, errorNotice, host, actions);
      overlay.append(dialog);

      let cropper = null;
      let cropperHost = null;
      let cropperReady = false;
      let finished = false;
      let exportPending = false;
      function restoreFocus() {
        if (previousFocus?.isConnected && typeof previousFocus.focus === 'function' && !previousFocus.disabled) previousFocus.focus({ preventScroll: true });
      }
      function cleanup(accepted) {
        if (finished) return;
        finished = true;
        document.removeEventListener('keydown', onKeyDown, true);
        document.removeEventListener('focusin', onFocusIn, true);
        overlay.removeEventListener('click', onBackdrop);
        cancel.removeEventListener('click', onCancel);
        useCrop.removeEventListener('click', onUse);
        for (const [key, value, priority] of previousBody) {
          if (value) document.body.style.setProperty(key, value, priority);
          else document.body.style.removeProperty(key);
        }
        if (previousRootOverflow[0]) document.documentElement.style.setProperty('overflow', ...previousRootOverflow);
        else document.documentElement.style.removeProperty('overflow');
        window.scrollTo(scrollPosition.x, scrollPosition.y);
        overlay.remove();
        activeDialog = false;
        if (!accepted) cropper?.destroy();
        restoreFocus();
      }
      function finish(accepted) {
        if (finished) return;
        const result = accepted ? cropper : null;
        if (accepted) cropper?.closeEditor();
        cleanup(Boolean(accepted));
        resolve(result);
      }
      function onCancel() { finish(false); }
      function showFailure(failure, selectedFile, stage, retained) {
        if (finished) return;
        errorNotice.textContent = options.formatError
          ? options.formatError(failure, selectedFile, retained)
          : 'We cannot open this photo. Choose another photo.';
        errorNotice.hidden = false;
        errorNotice.style.display = 'block';
        loading.hidden = true;
        if (!retained) host.style.display = 'none';
        options.onError?.(failure, selectedFile, stage);
        errorNotice.focus({ preventScroll: true });
        errorNotice.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
      }
      async function onUse() {
        if (useCrop.disabled || !cropper || exportPending) return;
        exportPending = true;
        useCrop.disabled = true;
        try {
          // Export while the dialog is still visible, so failure identifies
          // this exact photo instead of appearing after the modal closes.
          const preview = await cropper.toFile();
          if (finished) return;
          cropper.acceptedPreview = preview;
          finish(true);
        } catch (failure) {
          if (!finished) {
            const error = window.CustomifyPhotoUpload?.error('crop_export') || failure;
            showFailure(error, cropper.getFile(), 'crop-export', false);
          }
        } finally {
          exportPending = false;
          if (!finished) { useCrop.disabled = !cropper || !cropperReady; }
        }
      }
      function onBackdrop(event) { if (event.target === overlay) finish(false); }
      function focusableElements() {
        return [...dialog.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])')]
          .filter(element => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
      }
      function onKeyDown(event) {
        if (event.key === 'Escape') { event.preventDefault(); finish(false); return; }
        if (event.key !== 'Tab') return;
        const items = focusableElements();
        if (!items.length) { event.preventDefault(); heading.focus(); return; }
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
      function onFocusIn(event) { if (!dialog.contains(event.target)) heading.focus(); }

      cancel.addEventListener('click', onCancel);
      useCrop.addEventListener('click', onUse);
      overlay.addEventListener('click', onBackdrop);
      document.addEventListener('keydown', onKeyDown, true);
      document.addEventListener('focusin', onFocusIn, true);
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('position', 'fixed', 'important');
      document.body.style.setProperty('top', `${-scrollPosition.y}px`, 'important');
      document.body.style.setProperty('left', `${-scrollPosition.x}px`, 'important');
      document.body.style.setProperty('width', '100%', 'important');
      document.body.style.overflow = 'hidden';
      document.body.append(overlay);
      heading.focus({ preventScroll: true });
      try {
        cropperHost = document.createElement('div');
        host.append(cropperHost);
        cropper = mount(cropperHost, file, { alt: options.alt || 'Photo crop preview', initialRect: options.initialRect, aspectRatio: options.aspectRatio });
        const initialCropper = cropper;
        cropper.ready().then(() => {
          if (finished || cropper !== initialCropper) return;
          cropperReady = true;
          if (!exportPending) { loading.hidden = true; useCrop.disabled = false; }
        }).catch(error => {
          if (finished || cropper !== initialCropper) return;
          initialCropper.destroy();
          cropper = null;
          cropperReady = false;
          useCrop.disabled = true;
          if (!exportPending) showFailure(error, file, 'crop-open', false);
        });
      } catch (error) {
        cropper?.destroy();
        cropper = null;
        showFailure(error, file, 'crop-open', false);
      }
    });
  }

  return { mount, openDialog, fitAspectRect, resizeAspectRect, normalizeRect, moveRect, resizeRect, buildEntries, buildRequestMetadata, acceptedPhotoCount, firstEmptySlot, canReuseFullFrameFile };
});
